'use client';

import * as z from 'zod';

export const tenantSchema = z.object({
  id: z.string().optional(),
  tenant_code: z
    .string()
    .min(2, 'Tenant code must be at least 2 characters')
    .max(50, 'Tenant code must be less than 50 characters')
    .regex(
      /^[A-Za-z0-9_-]+$/,
      'Tenant code can only contain letters, numbers, underscores, and hyphens'
    ),
  tenant_name: z
    .string()
    .min(2, 'Tenant name must be at least 2 characters')
    .max(100, 'Tenant name must be less than 100 characters'),
  status: z.union([z.number(), z.string().transform((val) => Number(val))]),
  business_name: z
    .string()
    .min(2, 'Business name must be at least 2 characters')
    .max(200, 'Business name must be less than 200 characters'),
  tax_id: z.string().optional(),
  address_line1: z.string().min(1, 'Address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  country: z.string().min(1, 'Country is required'),
  postal_code: z.string().min(1, 'Postal code is required'),
  first_name: z.string().min(1, 'First name is required').max(100, 'First name must be less than 100 characters'),
  last_name: z.string().min(1, 'Last name is required').max(100, 'Last name must be less than 100 characters'),
  phone: z.string().nullable().optional(),
  email: z.string().email('Invalid email address'),
  website: z
    .string()
    .refine((value) => !value || /^[a-zA-Z]+:\/\//.test(value), 'Invalid website URL')
    .optional()
    .or(z.literal('')),
  logo: z.string().optional(),
  color_scheme: z.string().optional(),
  // subscription_start_date: z.string(),
  // subscription_end_date: z.string(),
  // billing_cycle: z.string(),
});

export const customerSchema = z.object({
  id: z.string().optional(),
  customer_code: z
    .string()
    .min(2, 'Customer code must be at least 2 characters')
    .max(50, 'Customer code must be less than 50 characters')
    .regex(
      /^[A-Za-z0-9_-]+$/,
      'Customer code can only contain letters, numbers, underscores, and hyphens'
    ),
  customer_name: z
    .string()
    .min(2, 'Customer name must be at least 2 characters')
    .max(100, 'Customer name must be less than 100 characters'),
  status: z.union([z.number(), z.string().transform((val) => Number(val))]),
  business_name: z
    .string()
    .min(2, 'Business name must be at least 2 characters')
    .max(200, 'Business name must be less than 200 characters')
    .optional(),
  tax_id: z.string().optional(),
  address_line1: z.string().min(1, 'Address is required').optional(),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required').optional(),
  state: z.string().min(1, 'State is required').optional(),
  country: z.string().min(1, 'Country is required').optional(),
  postal_code: z.string().min(1, 'Postal code is required').optional(),
  first_name: z.string().min(1, 'First name is required').max(100, 'First name must be less than 100 characters'),
  last_name: z.string().min(1, 'Last name is required').max(100, 'Last name must be less than 100 characters'),
  phone: z.string().nullable().optional(),
  email: z.string().email('Invalid email address').optional(),
  website: z
    .string()
    .refine((value) => !value || /^[a-zA-Z]+:\/\//.test(value), 'Invalid website URL')
    .optional()
    .or(z.literal('')),
  logo: z.string().optional(),
  color_scheme: z.string().optional(),
  account_manager_id: z.union([z.string().uuid(), z.string().length(0), z.null()]).optional(),
  parent_customer_id: z.union([z.string().uuid(), z.string().length(0), z.null()]).optional(),
  email_verified: z.string().optional(),
  tenant_id: z.string().uuid().optional(),
  customer_type: z.string().optional()
});

export const userSchema = z.object({
  id: z.string().optional(),
  email: z
    .string()
    .email('Invalid email address')
    .max(255, 'Email must be less than 255 characters'),
  first_name: z.string().max(100, 'First name must be less than 100 characters').optional(),
  last_name: z.string().max(100, 'Last name must be less than 100 characters').optional(),
  status: z.union([z.number(), z.string().transform((val) => Number(val))]),
  phone: z.string().max(50, 'Phone number must be less than 50 characters').nullable().optional(),
  is_system_user: z.boolean().optional().default(false),
  avatar: z.string().optional(),
  avatar_path: z.string().optional(),
  is_active: z.boolean().optional().default(true),
  user_timezone: z.string().optional(),
  welcome_email_sent: z.boolean().optional().default(false),
  welcome_survey_completed: z.boolean().optional().default(false),
  role_id: z.string().uuid('Role is required'),
  role_name: z.string().optional(),
  language: z.string().optional().default('en'),
  emailNotifications: z.record(z.string(), z.record(z.string(), z.boolean())),
  emailDigestFrequency: z
    .enum(['INSTANT', 'DAILY', 'WEEKLY', 'MONTHLY', 'NEVER'])
    .default('MONTHLY')
});

const baseNoteSchema = {
  id: z.string().uuid('Invalid ID format').optional(),
  is_active: z.boolean().optional().default(true),
  is_deleted: z.boolean().optional().default(false),
  created_at: z.date().optional(),
  created_by: z.string().uuid('Invalid creator ID format'),
  updated_at: z.date().optional(),
  modified_by: z.string().uuid('Invalid modifier ID format'),
  content: z
    .string()
    .min(1, 'Content cannot be empty')
    .max(65535, 'Content must be less than 65535 characters')
};

export const vulcanNoteSchema = z.object({
  ...baseNoteSchema,
  vulcan_id: z.string().uuid('Invalid vulcan ID format')
});

export const userNoteSchema = z.object({
  ...baseNoteSchema,
  user_id: z.string().uuid('Invalid user ID format')
});

export const EmailFormSchema = z.object({
  email: z.string().email()
});

export const ContactUsSchema = z.object({
  firstname: z.string().min(2, {
    message: 'Firstname must be at least 2 characters.'
  }),
  lastname: z.string().min(2, {
    message: 'Lastname must be at least 2 characters.'
  }),
  email: z.string().email({
    message: 'Please enter a valid email address.'
  }),
  /*phone: z.string().min(10, {
    message: 'Phone number must be at least 10 characters.'
  }),*/
  phone: z.string().optional(),  
  company_name: z.string().min(2, {
    message: 'Company name must be at least 2 characters.'
  }),
  company_website: z.string().optional(
    /*2, {
    message: 'Company website must be at least 2 characters.'
  }*/),
  message: z.string().min(10, {
    message: 'Message must be at least 10 characters '
  })
});

export const NewsletterSubscribersSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

export const ProfileFormSchema = z.object({
  first_name: z.string().max(100, 'First name must be less than 100 characters').optional(),
  last_name: z.string().max(100, 'Last name must be less than 100 characters').optional(),
  phone: z.string().max(50, 'Phone number must be less than 50 characters').optional().nullable(),
  email: z
    .string()
    .min(1, { message: 'Email is required.' })
    .email('This is not a valid email.')
    .optional(),
  language: z.string().min(1, { message: 'Please select a language.' }).default('en'),
  timezone: z.string().optional(),
  emailNotifications: z.record(z.string(), z.record(z.string(), z.boolean())),
  emailDigestFrequency: z
    .enum(['INSTANT', 'DAILY', 'WEEKLY', 'MONTHLY', 'NEVER'])
    .default('MONTHLY'),
  avatar: z.string().optional().nullable(),
  avatarPath: z.string().optional().nullable()
});

export const surveyFormSchema = z.object({
  transportation_modes: z.array(z.string()).min(1, "Select at least one mode"),
  volume_range: z.string().min(1, "Please select a volume range"),
  primary_interests: z.array(z.string()).min(1, "Select at least one interest"),
  price_per_user: z.string().min(1, "Please select a price range"),
  challenges: z.array(z.string()).min(1, "Select at least one challenge")
});

// Phone number validation regex for US formats
const phoneRegex = /^(\+1\s?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/;

export const partyFormSchema = z.object({
  party_id: z.number().optional(),
  location_id: z.number().optional(),

  // Hidden fields (optional since set by backend)
  party_code: z.string().optional(),
  party_source: z.string().optional(),

  // Basic Information
  party_name: z
    .string()
    .min(1, 'Company name is required')
    .max(200, 'Company name must be less than 200 characters'),

  party_types: z
    .string()
    .min(1, 'Party type is required'),

  // Location Information
  default_location_name: z
    .string()
    .min(1, 'Location name is required')
    .max(200, 'Location name must be less than 200 characters'),

  default_street_address: z
    .string()
    .min(1, 'Street address is required')
    .max(200, 'Street address must be less than 200 characters'),

  default_city_state_zip: z
    .string()
    .min(1, 'City, State, ZIP is required')
    .max(150, 'City, State, ZIP must be less than 150 characters'),

  default_country: z
    .string()
    .min(1, 'Country is required')
    .max(100, 'Country must be less than 100 characters'),

  default_location_notes: z
    .string()
    .max(500, 'Notes must be less than 500 characters')
    .optional(),

  // Contact Information
  first_name: z
    .string()
    .max(100, 'First name must be less than 100 characters'),

  last_name: z
    .string()
    .max(100, 'Last name must be less than 100 characters'),

  phone_number: z
    .string()
    .regex(phoneRegex, 'Please enter a valid phone number (e.g., (123) 456-7890)')
    .optional(),

  email: z
    .string()
    .email('Please enter a valid email address')
    .max(200, 'Email must be less than 200 characters')
    .optional(),

  latitude: z
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90'),

  longitude: z
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180'),

  vulcan_id: z.string().uuid('Invalid Vulcan ID format')
});

export type PartyFormData = z.infer<typeof partyFormSchema>;
export type VulcanNote = z.infer<typeof vulcanNoteSchema>;
export type UserNote = z.infer<typeof userNoteSchema>;
export type TenantFormData = z.infer<typeof tenantSchema>;
export type CustomerFormData = z.infer<typeof customerSchema>;
export type UserFormData = z.infer<typeof userSchema>;
export type EmailFormValues = z.infer<typeof EmailFormSchema>;
export type ContactUs = z.infer<typeof ContactUsSchema>;
export type NewsletterSubscribers = z.infer<typeof NewsletterSubscribersSchema>;
export type Profile = z.infer<typeof ProfileFormSchema>;
export type SurveyFormData = z.infer<typeof surveyFormSchema>;

